// @ts-check
// Chamadas da API de cobrança, LGPD, auditoria e do painel interno de suporte
// (docs/arquitetura-appcrm/08-contrato-cobranca-lgpd-operacao.md).
import { api, requestFile } from './api-client.js';

/**
 * @typedef {{ key: string, name: string, description: string, priceCents: number, maximumMembers: number | null,
 *   maximumPeople: number | null, features: string[], monthlyPriceCents?: number, modules?: string[], period?: 'MONTHLY' | 'ANNUAL', isLegacy?: boolean }} BillingPlan
 * @typedef {{ status: string, accessMode: 'FULL' | 'WARNING' | 'READ_ONLY', reason: string | null, trialDaysLeft: number | null,
 *   readOnlyAt: string | null }} SubscriptionState
 * @typedef {{ planKey: string, status: string, trialEndsAt: string | null, currentPeriodEndsAt: string | null, pastDueSince: string | null,
 *   suspendedAt: string | null, canceledAt: string | null, billingType: string | null, billingName: string | null, billingEmail: string | null,
 *   billingDocument: string | null, hasProviderSubscription: boolean }} Subscription
 * @typedef {{ asaasPaymentId: string, status: string, valueCents: number, dueDate: string | null, paidAt: string | null,
 *   billingType: string | null, invoiceUrl: string | null }} BillingPayment
 * @typedef {{ id: string, number: string | null, effectiveDate: string | null, value: number, serviceDescription: string | null,
 *   pdfUrl: string | null, xmlUrl: string | null }} BillingInvoice
 * @typedef {{ subscription: Subscription | null, state: SubscriptionState, plans: BillingPlan[], payments: BillingPayment[],
 *   invoices: BillingInvoice[], isProviderConfigured: boolean }} BillingOverview
 * @typedef {{ id: string, createdAt: string, action: string, actorType: string, actorWorkspaceMemberId: string | null, actorName: string | null,
 *   objectNameSingular: string | null, recordId: string | null, recordLabel: string | null, updatedFields: string[] | null,
 *   details: Record<string, unknown> }} AuditEntry
 */

/** @returns {Promise<BillingOverview>} */
export async function fetchBillingOverview() {
  return (await api.get('/rest/billing/subscription')).data;
}

/**
 * @param {{ planKey: string, billingType: string, billingName: string, billingEmail: string, billingDocument: string }} input
 * @returns {Promise<{ subscription: Subscription, state: SubscriptionState, invoiceUrl: string | null }>}
 */
export async function checkoutSubscription(input) {
  return (await api.post('/rest/billing/checkout', input)).data;
}

export async function cancelSubscription() {
  return (await api.delete('/rest/billing/subscription')).data;
}

/**
 * @param {{ limit?: number, startingAfter?: string | null, action?: string, objectNameSingular?: string, recordId?: string,
 *   actorWorkspaceMemberId?: string, since?: string, until?: string }} [filters]
 * @returns {Promise<{ entries: AuditEntry[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function fetchAuditLog(filters = {}) {
  return (await api.get('/rest/audit-log', /** @type {Record<string, unknown>} */ (filters))).data;
}

/**
 * Exportação do titular: o JSON vem como objeto e vira arquivo no navegador; o CSV já vem como arquivo.
 * @param {string} personId @param {'json' | 'csv'} format
 * @returns {Promise<{ blob: Blob, fileName: string }>}
 */
export async function exportDataSubject(personId, format) {
  const fileName = `titular-${personId}.${format}`;
  if (format === 'csv') {
    const { blob } = await requestFile(`/rest/privacy/people/${personId}/export`, { format: 'csv' });
    return { blob, fileName };
  }
  const body = await api.get(`/rest/privacy/people/${personId}/export`, { format: 'json' });
  return { blob: new Blob([JSON.stringify(body, null, 2)], { type: 'application/json' }), fileName };
}

/** @param {string} personId */
export async function anonymizeDataSubject(personId) {
  return (await api.post(`/rest/privacy/people/${personId}/anonymize`, { confirm: true })).data;
}

/** @param {string} personId */
export async function deleteDataSubject(personId) {
  return (await api.post(`/rest/privacy/people/${personId}/delete`, { confirm: true })).data;
}

/** @param {{ message: string, stack?: string, url: string }} report */
export async function reportClientError(report) {
  await api.post('/rest/client-errors', report, undefined, { skipUnauthorizedRedirect: true });
}

// Painel interno de suporte (portal, /admin)

const ADMIN_API = '/admin/api';

/** @param {string} email */
export async function requestAdminEmailCode(email) {
  return api.post(`${ADMIN_API}/sign-in/email-code`, { email }, undefined, { skipUnauthorizedRedirect: true });
}

/** @param {string} email @param {string} code */
export async function verifyAdminEmailCode(email, code) {
  return api.post(`${ADMIN_API}/sign-in/verify`, { email, code }, undefined, { skipUnauthorizedRedirect: true });
}

/** @returns {Promise<{ email: string } | null>} */
export async function fetchAdminSession() {
  try {
    return await api.get(`${ADMIN_API}/me`, undefined, { skipUnauthorizedRedirect: true });
  } catch (error) {
    if (/** @type {any} */ (error)?.statusCode === 401) return null;
    throw error;
  }
}

export async function signOutAdmin() {
  await api.post(`${ADMIN_API}/sign-out`, undefined, undefined, { skipUnauthorizedRedirect: true });
}

export async function fetchAdminTenants() {
  return (await api.get(`${ADMIN_API}/tenants`, undefined, { skipUnauthorizedRedirect: true })).data.tenants;
}

/** @param {string} tenantId */
export async function fetchAdminTenant(tenantId) {
  return (await api.get(`${ADMIN_API}/tenants/${tenantId}`, undefined, { skipUnauthorizedRedirect: true })).data;
}

/** @param {string} tenantId @param {{ status?: string, planKey?: string, trialEndsAt?: string }} changes */
export async function overrideAdminSubscription(tenantId, changes) {
  return (await api.patch(`${ADMIN_API}/tenants/${tenantId}/subscription`, changes)).data;
}

/** @param {string} tenantId @param {string} reason @returns {Promise<{ redirectUrl: string }>} */
export async function startSupportSession(tenantId, reason) {
  return (await api.post(`${ADMIN_API}/tenants/${tenantId}/support-session`, { reason }, undefined, { skipUnauthorizedRedirect: true })).data;
}

export async function fetchAdminAudit() {
  return (await api.get(`${ADMIN_API}/audit`, undefined, { skipUnauthorizedRedirect: true })).data.entries;
}

export async function fetchAdminBackups() {
  return (await api.get(`${ADMIN_API}/backups`, undefined, { skipUnauthorizedRedirect: true })).data.runs;
}

export async function runAdminBackups() {
  return (await api.post(`${ADMIN_API}/backups/run`, undefined, undefined, { skipUnauthorizedRedirect: true })).data;
}

// Códigos de acesso ao cadastro (contrato 08 de cobrança e operação, seção 7.1)

/**
 * @typedef {{ tenantId: string, tenantSlug: string | null, tenantDisplayName: string | null, tenantActivationStatus: string | null,
 *   email: string, redeemedAt: string }} SignUpCodeRedemption
 * @typedef {{ id: string, codeHint: string, label: string, note: string | null, allowedEmail: string | null, allowedEmailDomain: string | null,
 *   maxUses: number, useCount: number, subscriptionMode: 'TRIAL' | 'ACTIVE', planKey: string | null, trialDays: number | null, expiresAt: string,
 *   createdByEmail: string, createdAt: string, revokedAt: string | null, revokedByEmail: string | null, lastUsedAt: string | null,
 *   status: 'ACTIVE' | 'USED' | 'EXPIRED' | 'REVOKED', redemptions: SignUpCodeRedemption[] }} SignUpCode
 * @typedef {{ codes: SignUpCode[], plans: Array<{ key: string, name: string }>,
 *   defaults: { expiresInDays: number, maxUses: number, trialDays: number, planKey: string } }} SignUpCodeList
 * @typedef {{ label: string, note?: string, allowedEmail?: string, allowedEmailDomain?: string, expiresInDays?: number, maxUses?: number,
 *   subscriptionMode?: 'TRIAL' | 'ACTIVE', planKey?: string, trialDays?: number }} SignUpCodeInput
 */

/** @param {{ status?: string }} [filters] @returns {Promise<SignUpCodeList>} */
export async function listSignUpCodes(filters = {}) {
  return (await api.get(`${ADMIN_API}/sign-up-codes`, { status: filters.status ?? 'ALL', limit: 200 }, { skipUnauthorizedRedirect: true })).data;
}

/**
 * O código em claro só vem nesta resposta: a tela o guarda apenas no estado do diálogo.
 * @param {SignUpCodeInput} input @returns {Promise<{ signUpCode: SignUpCode, code: string, signUpUrl: string }>}
 */
export async function createSignUpCode(input) {
  return (await api.post(`${ADMIN_API}/sign-up-codes`, input, undefined, { skipUnauthorizedRedirect: true })).data;
}

/** @param {string} codeId @returns {Promise<{ signUpCode: SignUpCode }>} */
export async function revokeSignUpCode(codeId) {
  return (await api.post(`${ADMIN_API}/sign-up-codes/${codeId}/revoke`, undefined, undefined, { skipUnauthorizedRedirect: true })).data;
}

/**
 * @typedef {{ id: string, code: string, label: string, discountType: 'PERCENT' | 'FIXED', discountValue: number, appliesTo: 'PLAN' | 'ALL',
 *   duration: 'FIRST' | 'ALWAYS', validUntil: string | null, maxUses: number | null, usedCount: number, revokedAt: string | null,
 *   createdByEmail: string, createdAt: string, status: 'ACTIVE' | 'EXHAUSTED' | 'EXPIRED' | 'REVOKED', description: string }} SiteCoupon
 * @typedef {{ code: string, label: string, discountType: 'PERCENT' | 'FIXED', discountValue: number, appliesTo: 'PLAN' | 'ALL',
 *   duration: 'FIRST' | 'ALWAYS', validUntil?: string | null, maxUses?: number | null }} SiteCouponInput
 */

/** @returns {Promise<{ coupons: SiteCoupon[] }>} */
export async function listSiteCoupons() {
  return (await api.get(`${ADMIN_API}/site-coupons`, undefined, { skipUnauthorizedRedirect: true })).data;
}

/** @param {SiteCouponInput} input @returns {Promise<{ coupon: SiteCoupon }>} */
export async function createSiteCoupon(input) {
  return (await api.post(`${ADMIN_API}/site-coupons`, input, undefined, { skipUnauthorizedRedirect: true })).data;
}

/** @param {string} couponId @returns {Promise<{ coupon: SiteCoupon }>} */
export async function revokeSiteCoupon(couponId) {
  return (await api.post(`${ADMIN_API}/site-coupons/${couponId}/revoke`, undefined, undefined, { skipUnauthorizedRedirect: true })).data;
}
